import type { LearnModule } from './types'
import { alphabetLesson } from './alphabet'
import { harakatLessons } from './harakat'
import { noonRulesLessons } from './noonRules'
import { meemRulesLessons } from './meemRules'
import { qalqalahLesson } from './qalqalah'
import { laamRulesLessons } from './laamRules'
import { maddRulesLessons } from './maddRules'
import { otherRulesLessons } from './otherRules'

/**
 * The full "Learn How to Read" curriculum: a Qaida-style progression from
 * the bare alphabet through every rule covered in Tuhfatul Atfal, the
 * classical primer most beginner-to-intermediate tajweed courses are built
 * on. Modules are ordered so each one only assumes knowledge from earlier
 * modules - this order is the intended learning path, not just a table of
 * contents.
 */
export const learnCurriculum: LearnModule[] = [
  {
    id: 'foundations',
    title: '1. Foundations',
    description: 'The Arabic alphabet and its articulation points - start here.',
    lessons: [alphabetLesson],
  },
  {
    id: 'vowels-and-signs',
    title: '2. Vowels & Signs',
    description: 'Fatha, kasra, damma, sukun, tanween, shaddah, and the madd letters.',
    lessons: harakatLessons,
  },
  {
    id: 'noon-sakinah',
    title: '3. Noon Sakinah & Tanween',
    description: 'Izhar, idgham, iqlab, and ikhfa - what happens to a bare "n" sound.',
    lessons: noonRulesLessons,
  },
  {
    id: 'meem-sakinah',
    title: '4. Meem Sakinah',
    description: 'The labial counterpart to the noon rules: ikhfa, idgham, and izhar shafawi.',
    lessons: meemRulesLessons,
  },
  {
    id: 'qalqalah',
    title: '5. Qalqalah',
    description: 'The echoing/bouncing sound given to ق ط ب ج د whenever they carry sukun.',
    lessons: [qalqalahLesson],
  },
  {
    id: 'laam-rules',
    title: '6. Laam Rules',
    description: 'The laam in Allah\'s name, and sun letters vs. moon letters.',
    lessons: laamRulesLessons,
  },
  {
    id: 'madd-rules',
    title: '7. Madd (Elongation) Rules',
    description: 'Muttasil, munfasil, lazim, and \'aarid lis-sukoon - how far to stretch a madd letter.',
    lessons: maddRulesLessons,
  },
  {
    id: 'other-rules',
    title: '8. Everything Else',
    description: 'Raa\'s changing weight, always-heavy letters, and the waqf stop signs.',
    lessons: otherRulesLessons,
  },
]

export const allLessons = learnCurriculum.flatMap((m) => m.lessons)
export const totalLessonCount = allLessons.length

export function findLesson(lessonId: string) {
  for (const module of learnCurriculum) {
    const lesson = module.lessons.find((l) => l.id === lessonId)
    if (lesson) return { module, lesson }
  }
  return null
}

export function getModule(moduleId: string) {
  return learnCurriculum.find((m) => m.id === moduleId) || null
}

/** The lesson that follows `lessonId` in overall curriculum order, if any. */
export function getNextLesson(lessonId: string) {
  const idx = allLessons.findIndex((l) => l.id === lessonId)
  if (idx === -1 || idx === allLessons.length - 1) return null
  return allLessons[idx + 1]
}
